/**
 * @fileoverview Whether Grow can be put on this phone's home screen, and how to ask.
 *
 * Chrome on Android fires `beforeinstallprompt` when it decides a site is
 * installable, and the only way to keep that offer alive is to swallow the
 * event and hold on to it — once the handler returns without preventing the
 * default, the browser has made its own mind up and the chance is gone.
 *
 * iOS never fires it. Safari installs through the share sheet and there is no
 * API for it at all, so the honest thing is to say where the button is rather
 * than offer one that cannot work.
 *
 * Where: useTodayBrief and TipsContext (the device, deviceNow, which the
 * server's advice reads), TipViews and useTipAction (the install button).
 * API: none; browser events beforeinstallprompt and appinstalled.
 * Watch out: the listeners are added when this module is first imported,
 * not when a screen mounts, so an early offer is never missed.
 *
 * @module useInstallPrompt
 */

import { useCallback, useEffect, useState } from 'react';

/** Not in the DOM lib: Chrome's own event, and only Chrome's. */
interface BeforeInstallPromptEvent extends Event {
  /** Shows Chrome's install dialog; only from a tap, and only once per event. */
  prompt: () => Promise<void>;
  /** What the person chose in that dialog. */
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** The kind of device asking, for the install words. */
export type InstallPlatform = 'android' | 'ios' | 'desktop';

/** Whether Grow can be installed here, and how to ask. */
export interface InstallPrompt {
  /** What kind of device is asking, so the words can match it. */
  platform: InstallPlatform;
  /** Already running from the home screen, so there is nothing to offer. */
  installed: boolean;
  /** Chrome has an offer waiting. False on iOS always. */
  canPrompt: boolean;
  /** Shows Chrome's own dialog. Resolves true when they accepted. */
  promptInstall: () => Promise<boolean>;
}

/**
 * The kind of device, from its user agent.
 *
 * @returns android, ios (an iPad that says it is a Mac included) or desktop.
 */
function detectPlatform(): InstallPlatform {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  // iPadOS 13+ reports as a Mac, and the touch points are what give it away.
  if (/iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) return 'ios';
  return 'desktop';
}

/**
 * Whether Grow is running from the home screen already.
 *
 * @returns True in standalone display mode, or with Safari's navigator.standalone.
 */
function detectInstalled(): boolean {
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // Safari's own flag, which predates the standard and is still the only
  // signal on iOS.
  return ('standalone' in navigator) && Boolean((navigator as { standalone?: boolean }).standalone);
}

/*
 * Caught once for the whole app, as soon as this module loads.
 *
 * Chrome fires the event once, early, often before the screen that wants it
 * has mounted; a listener per screen missed it wherever the screen came
 * later, and two screens held two copies of one offer. Kept here, every
 * screen sees the same offer, and using it anywhere spends it everywhere.
 */
/** Chrome's offer, held until it is used. */
let offer: BeforeInstallPromptEvent | null = null;
/** Installed in this visit (appinstalled), before the display mode says so. */
let installedNow = false;
/** The screens to draw again when the offer comes or goes. */
const listeners = new Set<() => void>();
/** Tells every screen the offer has changed. */
const announce = (): void => { listeners.forEach((listener) => listener()); };

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event: Event) => {
    // Without this the browser shows its own banner on its own schedule and
    // never gives us the event again.
    event.preventDefault();
    offer = event as BeforeInstallPromptEvent;
    announce();
  });
  window.addEventListener('appinstalled', () => { installedNow = true; offer = null; announce(); });
}

/**
 * The device the app is on, as the server's advice reads it: opened from the
 * home screen, else a browser on Android, an iPhone or iPad, or a computer.
 *
 * @returns home_screen_app, android, iphone or computer.
 */
export function deviceNow(): 'android' | 'iphone' | 'computer' | 'home_screen_app' {
  if (installedNow || detectInstalled()) return 'home_screen_app';
  const platform = detectPlatform();
  return platform === 'android' ? 'android' : platform === 'ios' ? 'iphone' : 'computer';
}

/**
 * Whether Grow can be installed here, drawn again when Chrome's offer comes or is spent.
 *
 * @returns The platform, whether it is installed, whether Chrome has an offer, and the way to show it.
 */
export function useInstallPrompt(): InstallPrompt {
  const [, setVersion] = useState(0);
  const platform = detectPlatform();

  useEffect(() => {
    /** Draws the screen again. */
    const listener = (): void => setVersion((v) => v + 1);
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, []);

  /** Shows Chrome's own install dialog; true when they accepted. */
  const promptInstall = useCallback(async (): Promise<boolean> => {
    const held = offer;
    if (!held) return false;
    let outcome: 'accepted' | 'dismissed';
    try {
      await held.prompt();
      ({ outcome } = await held.userChoice);
    } catch {
      // Chrome refused to show it (not from a tap, or already shown): callers
      // do not wait for this, so a rejection would surface as an uncaught error.
      return false;
    }
    // Spent either way: the same event cannot be shown twice.
    offer = null;
    announce();
    return outcome === 'accepted';
  }, []);

  return { platform, installed: installedNow || detectInstalled(), canPrompt: offer !== null, promptInstall };
}
